/**
 * Role Detail Page
 * 
 * Displays detailed information for a single role including:
 * - Role overview and purpose
 * - Stage responsibilities (with links to Pipeline stages)
 * - SLA ownership (with links to SLA Catalogue)
 * - Tools & Access
 * - Escalation duties
 * - Quality checklist
 * - Training requirements
 * 
 * All data is driven by the consolidated Markdown registry.
 */

import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  ArrowLeft, 
  Users, 
  User,
  Building2,
  Target,
  AlertTriangle,
  CheckCircle,
  BookOpen,
  Wrench,
  Shield,
  GitBranch,
  FileText,
  ChevronRight,
  Headphones,
  Calculator,
  Truck,
  Megaphone,
  Database,
  Phone,
  UserCog,
  UserCheck,
  Crown,
  Car,
  Search,
  Briefcase,
  ClipboardList,
} from 'lucide-react';
import { 
  getRoleById, 
  getRoleGroups,
  getRolesForStage,
} from '../../lib/rolesRegistry';
import { getStageById } from '../../lib/leadPipelineRegistry';
import { getSOPsForRole } from '../../lib/sopsRegistry';
import { getChannelSLAById, getStageSLAById } from '../../lib/slaCatalogueRegistry';
import type { RACIType, RoleToolAccess } from '../../types';

// =============================================================================
// Icon Mapping
// =============================================================================

const iconMap: Record<string, React.ElementType> = {
  Headphones,
  Users,
  Calculator,
  Truck,
  Megaphone,
  Database,
  Building2,
  Phone,
  CheckCircle,
  UserCog,
  UserCheck,
  Crown,
  Car,
  Search,
  Shield,
  Briefcase,
  Target,
  ClipboardList,
};

function getIcon(iconName?: string): React.ElementType {
  if (!iconName) return Users;
  return iconMap[iconName] || Users;
}

// =============================================================================
// Sub-Components
// =============================================================================

// RACI Badge Component
const RACIBadge: React.FC<{ value: RACIType; size?: 'sm' | 'md' }> = ({ value, size = 'md' }) => {
  const colors: Record<RACIType, string> = {
    R: 'bg-blue-500 text-white',
    A: 'bg-purple-500 text-white',
    C: 'bg-amber-500 text-white',
    I: 'bg-gray-400 text-white',
  };
  
  const labels: Record<RACIType, { en: string; ar: string }> = {
    R: { en: 'Responsible', ar: 'مسؤول' },
    A: { en: 'Accountable', ar: 'مسؤول أعلى' },
    C: { en: 'Consulted', ar: 'مستشار' },
    I: { en: 'Informed', ar: 'مُطلع' },
  };
  
  const sizeClasses = size === 'sm' 
    ? 'w-6 h-6 text-xs' 
    : 'w-8 h-8 text-sm';
  
  return (
    <div className="flex items-center gap-2">
      <span 
        className={`inline-flex items-center justify-center ${sizeClasses} rounded-full font-bold ${colors[value]}`}
        title={labels[value].en}
      >
        {value}
      </span>
      <span className="text-sm text-gray-600 dark:text-gray-400">
        {labels[value].en}
      </span>
    </div>
  );
};

// Access Level Badge
const AccessLevelBadge: React.FC<{ level: RoleToolAccess['accessLevel']; isArabic: boolean }> = ({ level, isArabic }) => {
  const colors = {
    full: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    limited: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    view_only: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  };
  
  const labels = {
    full: isArabic ? 'وصول كامل' : 'Full Access',
    limited: isArabic ? 'وصول محدود' : 'Limited',
    view_only: isArabic ? 'عرض فقط' : 'View Only',
  };
  
  return (
    <span className={`px-2 py-1 rounded text-xs font-medium ${colors[level]}`}>
      {labels[level]}
    </span>
  );
};

// Section Card Component
const SectionCard: React.FC<{
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  variant?: 'default' | 'warning' | 'info';
}> = ({ title, icon, children, variant = 'default' }) => {
  const variants = {
    default: 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700',
    warning: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
    info: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
  };
  
  const titleColors = {
    default: 'text-gray-900 dark:text-white',
    warning: 'text-amber-900 dark:text-amber-100',
    info: 'text-blue-900 dark:text-blue-100',
  };
  
  return (
    <div className={`rounded-xl border p-6 ${variants[variant]}`}>
      <h2 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${titleColors[variant]}`}>
        {icon}
        {title}
      </h2>
      {children}
    </div>
  );
};

// =============================================================================
// Main Component
// =============================================================================

const RoleDetail: React.FC = () => {
  const { roleId } = useParams<{ roleId: string }>();
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';

  // Validate params
  if (!roleId) {
    return <Navigate to="/roles" replace />;
  }

  // Get role data
  const role = getRoleById(roleId);
  const roleGroups = getRoleGroups();

  if (!role) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Users className="w-16 h-16 text-gray-400 mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          {isArabic ? 'الدور غير موجود' : 'Role Not Found'}
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mb-6">
          {isArabic 
            ? `الدور "${roleId}" غير موجود في السجل`
            : `The role "${roleId}" was not found in the registry`
          }
        </p>
        <Link 
          to="/roles" 
          className="text-emerald-600 hover:text-emerald-700 flex items-center gap-2"
        >
          <ArrowLeft className={`w-4 h-4 ${isArabic ? 'rotate-180' : ''}`} />
          {isArabic ? 'العودة إلى الأدوار' : 'Back to Roles'}
        </Link>
      </div>
    );
  }

  // Get role group details
  const group = roleGroups.find(g => g.id === role.group);
  const RoleIcon = getIcon(role.icon || group?.icon);

  // Separate responsibilities by type
  const primaryResponsibilities = role.responsibilitiesByStage.filter(r => r.responsibilityType === 'R');
  const accountableStages = role.responsibilitiesByStage.filter(r => r.responsibilityType === 'A');
  const consultedStages = role.responsibilitiesByStage.filter(r => r.responsibilityType === 'C');
  const informedStages = role.responsibilitiesByStage.filter(r => r.responsibilityType === 'I');

  // Collect all SLA references
  const allSLARefs = new Set<string>();
  role.responsibilitiesByStage.forEach(resp => {
    resp.ownedSLAs?.forEach(sla => allSLARefs.add(sla));
  });

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-3 text-sm">
        <Link 
          to="/roles" 
          className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 flex items-center gap-1"
        >
          <ArrowLeft className={`w-4 h-4 ${isArabic ? 'rotate-180' : ''}`} />
          {isArabic ? 'الأدوار والمسؤوليات' : 'Roles & Responsibilities'}
        </Link>
        <span className="text-gray-400">/</span>
        <span className="text-gray-900 dark:text-white font-medium">
          {isArabic ? role.titleAr : role.title}
        </span>
      </div>

      {/* Hero Header */}
      <div 
        className="rounded-2xl p-8 text-white relative overflow-hidden"
        style={{ 
          background: `linear-gradient(135deg, ${role.color || group?.color || '#10b981'} 0%, ${adjustColor(role.color || group?.color || '#10b981', -30)} 100%)`
        }}
      >
        <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))]" />
        <div className="relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-start gap-6">
            {/* Icon and Title */}
            <div className="flex items-start gap-4">
              <div className="p-4 bg-white/20 rounded-xl backdrop-blur-sm">
                <RoleIcon className="w-10 h-10" />
              </div>
              
              <div>
                {/* Group Badge */}
                <span className="inline-block px-3 py-1 bg-white/20 rounded-full text-sm font-medium mb-2">
                  {isArabic ? group?.labelAr : group?.label}
                </span>
                
                <h1 className="text-3xl font-bold mb-2">
                  {isArabic ? role.titleAr : role.title}
                </h1>
                
                {/* Department and Reports To */}
                <div className="flex flex-wrap gap-4 text-sm mt-3">
                  <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-lg">
                    <Building2 className="w-4 h-4" />
                    <span>{isArabic ? role.departmentAr : role.department}</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-lg">
                    <User className="w-4 h-4" />
                    <span>{isArabic ? 'يقدم التقارير إلى: ' : 'Reports to: '}{isArabic ? role.reportsToAr : role.reportsTo}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Brands */}
            <div className="lg:ml-auto">
              <div className="flex flex-wrap gap-2">
                {role.appliesToBrands.map(brand => (
                  <span 
                    key={brand}
                    className="px-3 py-1.5 bg-white/20 rounded-lg text-sm font-medium"
                  >
                    {brand}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Purpose & Scope */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard 
          title={isArabic ? 'الغرض' : 'Purpose'}
          icon={<Target className="w-5 h-5 text-emerald-500" />}
        >
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            {isArabic ? role.purposeAr : role.purpose}
          </p>
        </SectionCard>

        <SectionCard 
          title={isArabic ? 'النطاق' : 'Scope'}
          icon={<Shield className="w-5 h-5 text-blue-500" />}
        >
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            {isArabic ? role.scopeAr : role.scope}
          </p>
        </SectionCard>
      </div>

      {/* Stage Responsibilities with Pipeline Links */}
      <SectionCard 
        title={isArabic ? 'المسؤوليات حسب المرحلة' : 'Stage Responsibilities'}
        icon={<GitBranch className="w-5 h-5 text-purple-500" />}
      >
        {/* Summary */}
        <div className="flex flex-wrap gap-4 mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center font-bold">R</span>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {primaryResponsibilities.length} {isArabic ? 'مراحل مسؤول عنها' : 'Responsible stages'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-purple-500 text-white text-xs flex items-center justify-center font-bold">A</span>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {accountableStages.length} {isArabic ? 'مراحل محاسب عليها' : 'Accountable stages'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center font-bold">C</span>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {consultedStages.length} {isArabic ? 'مراحل استشارية' : 'Consulted stages'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-gray-400 text-white text-xs flex items-center justify-center font-bold">I</span>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {informedStages.length} {isArabic ? 'مراحل إعلامية' : 'Informed stages'}
            </span>
          </div>
        </div>

        {/* Detailed responsibilities */}
        <div className="space-y-4">
          {role.responsibilitiesByStage.map(resp => {
            const stage = getStageById(resp.stageId);
            if (!stage) return null;

            return (
              <div 
                key={resp.stageId}
                className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
              >
                {/* Stage header */}
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50">
                  <Link 
                    to={`/pipeline/stage/${resp.stageId}`}
                    className="flex items-center gap-3 hover:text-emerald-600 transition-colors"
                  >
                    <span 
                      className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white"
                      style={{ backgroundColor: stage.meta.color }}
                    >
                      {stage.meta.order}
                    </span>
                    <div>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {isArabic ? stage.meta.titleAr : stage.meta.title}
                      </span>
                      <ChevronRight className="inline-block w-4 h-4 ml-1 text-gray-400" />
                    </div>
                  </Link>
                  <RACIBadge value={resp.responsibilityType} size="sm" />
                </div>

                {/* Key actions */}
                {resp.keyActions.length > 0 && (
                  <div className="p-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {isArabic ? 'الإجراءات الرئيسية:' : 'Key Actions:'}
                    </h4>
                    <ul className="space-y-1">
                      {(isArabic ? resp.keyActionsAr : resp.keyActions).map((action, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                          <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                          {action}
                        </li>
                      ))}
                    </ul>

                    {/* Handover info */}
                    {(resp.handoverInFrom?.length || resp.handoverOutTo?.length) ? (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex flex-wrap gap-4 text-xs">
                        {resp.handoverInFrom && resp.handoverInFrom.length > 0 && (
                          <div className="flex items-center gap-1 text-gray-500">
                            <ArrowLeft className="w-3 h-3" />
                            <span>{isArabic ? 'يستلم من:' : 'Receives from:'}</span>
                            {resp.handoverInFrom.map(fromId => {
                              const fromRole = getRoleById(fromId);
                              return (
                                <Link 
                                  key={fromId}
                                  to={`/roles/${fromId}`}
                                  className="text-emerald-600 hover:underline"
                                >
                                  {isArabic ? fromRole?.titleAr : fromRole?.title || fromId}
                                </Link>
                              );
                            })}
                          </div>
                        )}
                        {resp.handoverOutTo && resp.handoverOutTo.length > 0 && (
                          <div className="flex items-center gap-1 text-gray-500">
                            <ChevronRight className="w-3 h-3" />
                            <span>{isArabic ? 'يسلم إلى:' : 'Hands off to:'}</span>
                            {resp.handoverOutTo.map(toId => {
                              const toRole = getRoleById(toId);
                              return (
                                <Link 
                                  key={toId}
                                  to={`/roles/${toId}`}
                                  className="text-emerald-600 hover:underline"
                                >
                                  {isArabic ? toRole?.titleAr : toRole?.title || toId}
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ) : null}

                    {/* Owned KPIs and SLAs */}
                    {(resp.ownedKPIs?.length || resp.ownedSLAs?.length) ? (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex flex-wrap gap-2">
                        {resp.ownedKPIs?.map(kpi => (
                          <span 
                            key={kpi}
                            className="px-2 py-1 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded text-xs"
                          >
                            KPI: {kpi}
                          </span>
                        ))}
                        {resp.ownedSLAs?.map(slaRef => {
                          const isChannelSLA = slaRef.startsWith('channel_');
                          const sla = isChannelSLA 
                            ? getChannelSLAById(slaRef)
                            : getStageSLAById(slaRef);
                          return (
                            <Link
                              key={slaRef}
                              to={`/sla-catalogue/${isChannelSLA ? 'channel' : 'stage'}/${slaRef}`}
                              className="px-2 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                            >
                              SLA: {sla ? (isArabic && 'channelAr' in sla ? sla.channelAr : 'channel' in sla ? sla.channel : sla.stageName) : slaRef}
                            </Link>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </SectionCard>

      {/* Two-column layout for Tools and Escalation */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tools & Access */}
        <SectionCard 
          title={isArabic ? 'الأدوات والوصول' : 'Tools & Access'}
          icon={<Wrench className="w-5 h-5 text-amber-500" />}
        >
          <div className="space-y-3">
            {role.toolsAccess.map((tool, idx) => (
              <div 
                key={idx}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
              >
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {isArabic ? tool.toolAr : tool.tool}
                  </p>
                  {tool.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {isArabic ? tool.descriptionAr : tool.description}
                    </p>
                  )}
                </div>
                <AccessLevelBadge level={tool.accessLevel} isArabic={isArabic} />
              </div>
            ))}
          </div>
        </SectionCard>

        {/* Escalation Duties */}
        <SectionCard 
          title={isArabic ? 'واجبات التصعيد' : 'Escalation Duties'}
          icon={<AlertTriangle className="w-5 h-5 text-red-500" />}
          variant="warning"
        >
          <div className="space-y-4">
            {role.escalationDuties.map((duty, idx) => (
              <div 
                key={idx}
                className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-amber-200 dark:border-amber-800"
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {isArabic ? duty.breachTypeAr : duty.breachType}
                  </h4>
                  <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded text-xs">
                    {isArabic ? duty.timeframeAr : duty.timeframe}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {isArabic ? duty.actionAr : duty.action}
                </p>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      {/* Quality Checklist */}
      <SectionCard 
        title={isArabic ? 'قائمة فحص الجودة' : 'Quality Checklist'}
        icon={<CheckCircle className="w-5 h-5 text-green-500" />}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(isArabic ? role.qualityChecklistAr : role.qualityChecklist).map((item, idx) => (
            <div 
              key={idx}
              className="flex items-start gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg"
            >
              <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-gray-700 dark:text-gray-300">{item}</span>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Training Requirements */}
      <SectionCard 
        title={isArabic ? 'متطلبات التدريب' : 'Training Requirements'}
        icon={<BookOpen className="w-5 h-5 text-indigo-500" />}
        variant="info"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(isArabic ? role.trainingRequiredAr : role.trainingRequired).map((training, idx) => (
            <div 
              key={idx}
              className="flex items-start gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-blue-200 dark:border-blue-800"
            >
              <BookOpen className="w-5 h-5 text-indigo-500 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-gray-700 dark:text-gray-300">{training}</span>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Templates Used */}
      {role.templatesUsed.length > 0 && (
        <SectionCard 
          title={isArabic ? 'القوالب المستخدمة' : 'Templates Used'}
          icon={<FileText className="w-5 h-5 text-cyan-500" />}
        >
          <div className="flex flex-wrap gap-2">
            {role.templatesUsed.map((template, idx) => (
              <span 
                key={idx}
                className="px-3 py-1.5 bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 rounded-lg text-sm"
              >
                {template}
              </span>
            ))}
          </div>
        </SectionCard>
      )}

      {/* Related SOPs */}
      {(() => {
        const roleSOPs = getSOPsForRole(role.id);
        if (roleSOPs.length === 0) return null;
        
        return (
          <SectionCard 
            title={isArabic ? 'إجراءات التشغيل الموحدة' : 'Standard Operating Procedures'}
            icon={<ClipboardList className="w-5 h-5 text-orange-500" />}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {roleSOPs.slice(0, 6).map(sop => (
                <Link
                  key={sop.meta.id}
                  to={`/sops/${sop.meta.id}`}
                  className="flex items-start gap-3 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors group"
                >
                  <div className="w-8 h-8 rounded-lg bg-orange-100 dark:bg-orange-900/40 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors truncate">
                      {isArabic ? sop.meta.titleAr : sop.meta.title}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {sop.meta.primaryOwnerRoleId === role.id 
                        ? (isArabic ? 'المسؤول الرئيسي' : 'Primary Owner')
                        : (isArabic ? 'داعم' : 'Supporting')
                      }
                    </p>
                  </div>
                  <ChevronRight className={`w-4 h-4 text-gray-400 group-hover:text-orange-500 transition-colors flex-shrink-0 ${isArabic ? 'rotate-180' : ''}`} />
                </Link>
              ))}
            </div>
            {roleSOPs.length > 6 && (
              <div className="mt-3 text-center">
                <Link
                  to="/sops"
                  className="text-sm text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 font-medium"
                >
                  {isArabic ? `عرض جميع الإجراءات (${roleSOPs.length}) ←` : `View all SOPs (${roleSOPs.length}) →`}
                </Link>
              </div>
            )}
          </SectionCard>
        );
      })()}

      {/* Related Roles - Same Group */}
      {(() => {
        const relatedRoles = getRolesForStage(primaryResponsibilities[0]?.stageId || '')
          .filter(r => r.id !== role.id)
          .slice(0, 4);
        
        if (relatedRoles.length === 0) return null;
        
        return (
          <SectionCard 
            title={isArabic ? 'الأدوار ذات الصلة' : 'Related Roles'}
            icon={<Users className="w-5 h-5 text-emerald-500" />}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {relatedRoles.map(relRole => {
                const relGroup = roleGroups.find(g => g.id === relRole.group);
                const RelIcon = getIcon(relRole.icon || relGroup?.icon);
                
                return (
                  <Link
                    key={relRole.id}
                    to={`/roles/${relRole.id}`}
                    className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-emerald-300 dark:hover:border-emerald-600 transition-colors"
                  >
                    <div 
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: `${relRole.color || relGroup?.color || '#10b981'}20` }}
                    >
                      <RelIcon 
                        className="w-5 h-5"
                        style={{ color: relRole.color || relGroup?.color || '#10b981' }}
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white truncate">
                        {isArabic ? relRole.titleAr : relRole.title}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {isArabic ? relGroup?.labelAr : relGroup?.label}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </SectionCard>
        );
      })()}

      {/* Navigation */}
      <div className="flex justify-between items-center pt-6 border-t border-gray-200 dark:border-gray-700">
        <Link 
          to="/roles" 
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-emerald-600 transition-colors"
        >
          <ArrowLeft className={`w-4 h-4 ${isArabic ? 'rotate-180' : ''}`} />
          {isArabic ? 'جميع الأدوار' : 'All Roles'}
        </Link>
        
        {primaryResponsibilities.length > 0 && (
          <Link 
            to={`/pipeline/stage/${primaryResponsibilities[0].stageId}`}
            className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700 transition-colors"
          >
            {isArabic ? 'عرض المرحلة الرئيسية' : 'View Primary Stage'}
            <ChevronRight className={`w-4 h-4 ${isArabic ? 'rotate-180' : ''}`} />
          </Link>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Adjust a hex color by a percentage (positive = lighter, negative = darker)
 */
function adjustColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  
  return '#' + (
    0x1000000 +
    (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
    (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
    (B < 255 ? B < 1 ? 0 : B : 255)
  ).toString(16).slice(1);
}

export default RoleDetail;
